import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import travellerObject from '@/entities/traveller/interface'
import onlinePaymentObject from '@/entities/onlinePayment/interface'

import {
  ALL_BOOKINGS_QUERY,
  SINGLE_BOOKING_QUERY,
  CREATE_BOOKING_MUTATION,
  UPDATE_BOOKING_MUTATION,
  DELETE_BOOKING_MUTATION,
  FORCE_DELETE_BOOKING_MUTATION,
  ADD_BOOKING_TRAVELLERS_MUTATION,
  REMOVE_BOOKING_TRAVELLERS_MUTATION,
  CREATE_BOOKING_TRAVELLERS_MUTATION,
  DELETE_BOOKING_TRAVELLERS_MUTATION,
  UPDATE_BOOKING_TRAVELLERS_ORDER_MUTATION,
  ADD_BOOKING_ONLINE_PAYMENTS_MUTATION,
  REMOVE_BOOKING_ONLINE_PAYMENTS_MUTATION,
  CREATE_BOOKING_ONLINE_PAYMENTS_MUTATION,
  DELETE_BOOKING_ONLINE_PAYMENTS_MUTATION,
  UPDATE_BOOKING_ONLINE_PAYMENTS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const bookingRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_BOOKING_QUERY, variables).then((data: any) => {
      return hydrateObject(data.booking)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_BOOKINGS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.bookings)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_BOOKING_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.booking),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_BOOKINGS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.bookings),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_BOOKING_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.booking)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_BOOKING_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.booking)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_BOOKING_MUTATION, variables).then((data: any) => {
      return data.deleteBooking
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_BOOKING_MUTATION, variables).then((data: any) => {
      return data.forceDeleteBooking
    })
  },

  assignTravellers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travellerIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_BOOKING_TRAVELLERS_MUTATION, variables)
  },

  unassignTravellers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travellerIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_BOOKING_TRAVELLERS_MUTATION, variables)
  },

  createTravellers: async (id: string, items: Array<travellerObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travellers: items,
      },
    }
    return graphQLClient.request(CREATE_BOOKING_TRAVELLERS_MUTATION, variables)
  },

  deleteTravellers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travellerIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_BOOKING_TRAVELLERS_MUTATION, variables)
  },

  updateTravellersOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travellerIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_BOOKING_TRAVELLERS_ORDER_MUTATION, variables)
  },

  assignOnlinePayments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        onlinePaymentIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_BOOKING_ONLINE_PAYMENTS_MUTATION, variables)
  },

  unassignOnlinePayments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        onlinePaymentIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_BOOKING_ONLINE_PAYMENTS_MUTATION, variables)
  },

  createOnlinePayments: async (id: string, items: Array<onlinePaymentObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        onlinePayments: items,
      },
    }
    return graphQLClient.request(CREATE_BOOKING_ONLINE_PAYMENTS_MUTATION, variables)
  },

  deleteOnlinePayments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        onlinePaymentIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_BOOKING_ONLINE_PAYMENTS_MUTATION, variables)
  },

  updateOnlinePaymentsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        onlinePaymentIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_BOOKING_ONLINE_PAYMENTS_ORDER_MUTATION, variables)
  },
}

export default bookingRepository
